import React, { useState, useRef, useEffect } from 'react';
import { FiX, FiSend } from 'react-icons/fi';
import api from '../config/api';

const AIChatWidget = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Welcome to Royal Garden Villas! How can I help you today? Ask me about our villas, pricing, or booking.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await api.post('/chat', { message: userMsg, session_id: sessionId });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      if (res.data.session_id) setSessionId(res.data.session_id);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again or call us at +91 9820088292.' }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div data-testid="chat-window" className="fixed bottom-24 right-6 z-[60] w-[360px] max-w-[calc(100vw-48px)] h-[500px] flex flex-col bg-white rounded-xl border border-gray-200 shadow-2xl">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 rounded-t-xl bg-[#1B2432]">
        <div>
          <p className="text-white text-sm font-semibold">Villa Concierge</p>
          <p className="text-white/50 text-xs">AI Assistant</p>
        </div>
        <button onClick={onClose} data-testid="chat-close-btn" className="text-white/40 hover:text-white transition-colors">
          <FiX className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-[#F7F8FA]">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed rounded-xl ${
              msg.role === 'user'
                ? 'bg-[#F57C20] text-white'
                : 'bg-white text-[#1A1A2E] border border-gray-100'
            }`}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 text-[#9CA3AF] text-sm">
              Typing...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={sendMessage} className="px-5 py-4 border-t border-gray-100 bg-white rounded-b-xl">
        <div className="flex gap-3">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Ask about villas, pricing..."
            data-testid="chat-input"
            className="flex-1 bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1A1A2E] placeholder-[#9CA3AF] focus:outline-none focus:border-[#F57C20]"
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            data-testid="chat-send-btn"
            className="w-12 h-12 flex items-center justify-center bg-[#F57C20] text-white rounded-lg disabled:opacity-30 hover:bg-[#E06A10] transition-colors"
          >
            <FiSend className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default AIChatWidget;
